import type * as THREE from "three";
import {
  scene,
  camera,
  renderer,
  composer,
  updateLights,
  handleResize,
  timing,
  initCore,
} from "./core";
import { updateRespawnEffects, updateMovementEffects } from "./effects";
import {
  getPlayerMeshes,
  getLocalPlayerId,
  getGhostMeshes,
  getGameState,
  getRocketMeshes,
  keys,
} from "./gameState";
import { initializeSocket, socket } from "./socket";
import {
  createUIElements,
  updateInstructions,
  createLatencyDisplay,
} from "./ui";
import { updateGameUI } from "./ui-updates";
import { initializeKeyboardControls } from "./gameState";
import Stats from "three/examples/jsm/libs/stats.module.js";
import { initializeMinimap, updateMinimap } from "./minimap";
// Import animation functions directly from model files
import { animatePlayer } from "./models/playerModel";
import { animateGhost } from "./models/ghostModel";
import { animateRocket } from "./models/rocketModel";
// Import the new processGameUpdates function
import { processGameUpdates } from "./processMovement";
// Import the new particle system
import { updateParticleSystems } from "./rendering/particleSystem";

// Animation flags and last time
let isAnimating = true;

// Initialize core systems (new)
initCore();

// Create UI elements
createUIElements();
updateInstructions();

// Initialize socket and keyboard controls
initializeSocket();
initializeKeyboardControls();

// Initialize minimap
initializeMinimap();

// Set up renderer
document.body.appendChild(renderer.domElement);

// Set up stats
const stats = new Stats();
document.body.appendChild(stats.dom);
stats.dom.style.position = "absolute";
stats.dom.style.bottom = "0px";
stats.dom.style.right = "0px";
stats.dom.style.top = "unset";
stats.dom.style.left = "unset";

// Create latency display
createLatencyDisplay();

// Animation loop - optimized with structured timing
function animate(time = 0) {
  requestAnimationFrame(animate);

  // Only process animation if the game is active and connected
  if (isAnimating && socket?.readyState === WebSocket.OPEN) {
    stats.begin();

    try {
      // HIGH PRIORITY - Run every frame
      if (timing.shouldUpdate("HIGH", time)) {
        // Core game state updates for smooth movement
        processGameUpdates();

        // Update lighting system
        updateLights(time);
      }

      // MEDIUM PRIORITY - Run every 3 frames
      if (timing.shouldUpdate("MEDIUM", time)) {
        // Visual effects that need moderate frequency
        updateRespawnEffects();

        // Update particle systems
        updateParticleSystems();

        // Animate all game objects
        animateGameObjects(time);

        // Update the minimap
        updateMinimap();
      }

      // LOW PRIORITY - Run every 5 frames
      if (timing.shouldUpdate("LOW", time)) {
        // Non-critical visual effects
        updateMovementEffects();

        // UI updates
        updateGameUI();
      }

      // Render the scene - always do this
      if (composer) {
        composer.render();
      } else {
        renderer.render(scene, camera);
      }
    } catch (error) {
      console.error("Error in animation loop:", error);
    }

    stats.end();
  } else {
    // Even when paused, render the scene but less frequently
    if (Math.floor(time / 160) % 3 === 0) {
      if (composer) {
        composer.render();
      } else {
        renderer.render(scene, camera);
      }
    }
  }
}

// Extract animation logic to reduce main loop complexity
function animateGameObjects(time: number): void {
  const gameState = getGameState();
  if (!gameState) return;

  // Animate player models
  const playerMeshes = getPlayerMeshes();
  for (const playerId in playerMeshes) {
    const playerMesh = playerMeshes[playerId];
    const isMoving =
      playerId === getLocalPlayerId()
        ? keys.w ||
          keys.s ||
          keys.a ||
          keys.d ||
          keys.ArrowUp ||
          keys.ArrowDown ||
          keys.ArrowLeft ||
          keys.ArrowRight
        : true;

    animatePlayer(playerMesh, time, isMoving);
  }

  // Animate ghost models
  const ghostMeshes = getGhostMeshes();
  for (const ghostId in ghostMeshes) {
    const ghostMesh = ghostMeshes[ghostId];
    animateGhost(ghostMesh as unknown as THREE.Group, time);
  }

  // Animate rocket models
  const rocketMeshes = getRocketMeshes();
  for (const rocketMesh of rocketMeshes) {
    animateRocket(rocketMesh, time);
  }
}

// Start animation loop
animate();

// Handle window resize
window.addEventListener("resize", handleResize);

// Handle visibility change
document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    console.log("Game paused (tab inactive)");
    isAnimating = false;
  } else {
    console.log("Game resumed (tab active)");
    isAnimating = true;
  }
});
